import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '@app/components/Layout';
import { StaffNameGate, staffNameNeedsSetup } from '@app/components/StaffNameGate';
import { BookingFlow } from '@features/booking/components/BookingFlow';
import { AdminDashboard } from '@features/admin/components/AdminDashboard';
import { CompanyDashboard } from '@features/company-dashboard/components/CompanyDashboard';
import { TechnicianView } from '@features/technician/components/TechnicianView';
import { CustomerPortal } from '@features/customer-portal/components/CustomerPortal';
import { LandingPage } from '@features/home/components/LandingPage';
import { LoginModal } from '@features/auth/components/LoginModal';
import { VerificationPage } from '@features/verification/components/VerificationPage';
import { AlertModalHost, showAlertModal } from '@shared/components/common/AlertModal';
import { ToastHost } from '@shared/components/common/Toast';
import { api } from './services/api';
import { supabase } from './lib/supabase';
import { setErrorReporterUserType } from './lib/errorReporter';
import { resolveLinkEventContext } from './lib/linkEvents';
import {
  validatePasswordPolicy,
  friendlyPasswordError,
  PASSWORD_POLICY_HINT
} from './lib/passwordPolicy';
import { Loader2, AlertTriangle, ShieldAlert } from 'lucide-react';
import { classifyStartupError, formatErrorRef, StartupError } from '@/lib/startupError';
import { PasswordInput } from '@shared/components/ui/PasswordInput';
import {
  Appointment,
  Service,
  Venue,
  AddOn,
  User,
  Company,
  Customer,
  UserGroup,
  SystemConfig,
  MedicalCertificate,
  QuestionnaireTemplate,
  QuestionnaireSubmission
} from './types';

export default function App() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [addOns, setAddOns] = useState<AddOn[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireTemplate[]>([]);
  const [submissions, setSubmissions] = useState<QuestionnaireSubmission[]>([]);
  const [certificates, setCertificates] = useState<MedicalCertificate[]>([]);
  const [systemConfig, setSystemConfig] = useState<SystemConfig | undefined>(undefined);

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [currentCustomer, setCurrentCustomer] = useState<Customer | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [startupError, setStartupError] = useState<StartupError | null>(null);
  const [anyDataLoaded, setAnyDataLoaded] = useState(false);
  const [apiErrors, setApiErrors] = useState<Record<string, string>>({});
  const silentLoadCountRef = useRef(0);
  const linkEventSentRef = useRef(false);
  const isResetModeRef = useRef(
    new URLSearchParams(window.location.search).get('mode') === 'reset'
  );

  const [view, setView] = useState<'home' | 'booking'>('home');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginContext, setLoginContext] = useState<'staff' | 'company' | 'customer'>('staff');
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [recoveryConfirmPassword, setRecoveryConfirmPassword] = useState('');
  const [recoveryError, setRecoveryError] = useState('');
  const [isUpdatingRecovery, setIsUpdatingRecovery] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryStep, setRecoveryStep] = useState<'code' | 'password'>('code');
  const [showSignInCodeModal, setShowSignInCodeModal] = useState(false);
  const [signInEmail, setSignInEmail] = useState('');
  const [signInCode, setSignInCode] = useState('');
  const [signInError, setSignInError] = useState('');
  const [isVerifyingSignIn, setIsVerifyingSignIn] = useState(false);
  const [showCustomerPasswordGate, setShowCustomerPasswordGate] = useState(false);
  const [staffNameGateDismissed, setStaffNameGateDismissed] = useState(false);
  const [customerPassword, setCustomerPassword] = useState('');
  const [customerConfirmPassword, setCustomerConfirmPassword] = useState('');
  const [customerPasswordError, setCustomerPasswordError] = useState('');
  const [isUpdatingCustomerPassword, setIsUpdatingCustomerPassword] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const isVerifyMode = urlParams.get('mode') === 'verify';
  const isQuestionnaireMode = urlParams.get('mode') === 'questionnaire';
  const isCompanyLoginMode = urlParams.get('mode') === 'company-login';
  const questionnaireAppointmentId = isQuestionnaireMode
    ? urlParams.get('appointment') || null
    : null;

  const checkLinkError = () => {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const hashError = hashParams.get('error');
    const hashErrorCode = hashParams.get('error_code');
    if (hashError) {
      const isExpired = hashErrorCode === 'otp_expired' || hashError === 'access_denied';
      const message = isExpired
        ? 'Your link has expired or already been used. Please request a new one.'
        : 'The link is invalid. Please request a new one.';
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      showAlertModal(message);
    }
  };

  const clearRecoveryUrl = () => {
    isResetModeRef.current = false;
    const url = new URL(window.location.href);
    if (url.hash) {
      url.hash = '';
    }
    if (url.searchParams.get('mode') === 'reset') {
      url.searchParams.delete('mode');
    }
    if (url.searchParams.get('mode') === 'questionnaire') {
      url.searchParams.delete('mode');
      url.searchParams.delete('appointment');
    }
    if (url.searchParams.get('code')) {
      url.searchParams.delete('code');
    }
    if (url.searchParams.get('token')) {
      url.searchParams.delete('token');
    }
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const hydrateSession = async () => {
    try {
      const session = await api.getCurrentSession();
      if (!session) {
        setCurrentUser(null);
        setCurrentCompany(null);
        setCurrentCustomer(null);
        setCurrentSessionId(null);
        setShowCustomerPasswordGate(false);
        return;
      }
      setCurrentSessionId(session.sessionId || null);
      if (session.type === 'staff') {
        setCurrentUser(session.profile);
        setCurrentCompany(null);
        setCurrentCustomer(null);
        setShowCustomerPasswordGate(false);
      } else if (session.type === 'company') {
        setCurrentCompany(session.profile);
        setCurrentUser(null);
        setCurrentCustomer(null);
        setShowCustomerPasswordGate(false);
      } else if (session.type === 'customer') {
        setCurrentCustomer(session.profile);
        setCurrentUser(null);
        setCurrentCompany(null);
        setShowCustomerPasswordGate(Boolean(session.profile?.mustChangePassword));
      }
    } catch (e) {
      console.error('Hydration Failure:', e);
    }
  };

  const loadPublicBootstrap = async () => {
    const [config, reference] = await Promise.all([
      api.getPublicConfig(),
      api.fetchPublicReference()
    ]);
    setSystemConfig(config);
    setVenues(reference.venues || []);
    setServices(reference.services || []);
    setAddOns(reference.addOns || []);
    setUserGroups([]);
    setAppointments([]);
    setUsers([]);
    setCompanies([]);
    setCustomers([]);
    setQuestionnaires([]);
    setSubmissions([]);
    setCertificates([]);
    setApiErrors({});
  };

  const loadData = async (silent = false) => {
    if (silent) {
      silentLoadCountRef.current += 1;
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    try {
      const hasSession =
        import.meta.env.VITE_DEV_BYPASS_AUTH === 'true' ||
        (await supabase.auth.getSession()).data.session !== null;

      if (hasSession) {
        const data = await api.fetchInitialData();
        setVenues(data.venues || []);
        setServices(data.services || []);
        setAddOns(data.addOns || []);
        setAppointments(data.appointments || []);
        setUsers(data.users || []);
        setCompanies(data.companies || []);
        setCustomers(data.customers || []);
        setUserGroups(data.userGroups || []);
        setQuestionnaires(data.questionnaires || []);
        setSubmissions(data.submissions || []);
        setCertificates(data.certificates || []);
        setSystemConfig(data.systemConfig);
        setApiErrors(data.errors || {});
      } else {
        await loadPublicBootstrap();
      }

      await hydrateSession();
      setStartupError(null);
      setAnyDataLoaded(true);
    } catch (e: any) {
      console.error('Critical Startup Error:', e);
      setStartupError(classifyStartupError(e));
    } finally {
      if (silent) {
        silentLoadCountRef.current = Math.max(0, silentLoadCountRef.current - 1);
        if (silentLoadCountRef.current === 0) {
          setIsRefreshing(false);
        }
      } else {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    checkLinkError();

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryStep('password');
        setShowRecoveryModal(true);
        return;
      }
      if (event === 'SIGNED_IN') {
        if (isResetModeRef.current) {
          setRecoveryStep('password');
          setShowRecoveryModal(true);
          return;
        }
        hydrateSession();
        loadData(true);
        return;
      }
      if (event === 'SIGNED_OUT') {
        const suppress = localStorage.getItem('k2_suppress_signed_out');
        setCurrentSessionId(null);
        setCurrentUser(null);
        setCurrentCompany(null);
        setCurrentCustomer(null);
        setShowCustomerPasswordGate(false);
        setShowLoginModal(false);
        setView('home');
        if (suppress) {
          localStorage.removeItem('k2_suppress_signed_out');
          return;
        }
      } else if (session) {
        hydrateSession();
        loadData(true);
      }
    });

    if (isResetModeRef.current) {
      // On reset URLs we skip the full data load to avoid racing with the
      // auth event, but we still need to clear the loading screen and load
      // the public reference data so the page renders.
      loadPublicBootstrap().finally(() => setIsLoading(false));
    } else {
      loadData();
    }

    return () => subscription.unsubscribe();
  }, []);

  // A questionnaire deep link (?mode=questionnaire&appointment=...) auto-opens
  // the paperwork once a customer session exists, but an unauthenticated
  // visitor would otherwise be dropped on the public booking page with no hint
  // of what to do next. Open the customer sign-in flow for them; the URL params
  // survive sign-in, so the portal then opens the right questionnaire.
  useEffect(() => {
    if (!isQuestionnaireMode || isLoading) return;
    if (currentUser || currentCompany || currentCustomer) return;
    setLoginContext('customer');
    setShowLoginModal(true);
    // Only after the initial load settles — session state is final by then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Company welcome emails deep-link here (?mode=company-login) so a new team
  // member lands on the Corporate Portal sign-in instead of the patient one.
  useEffect(() => {
    if (!isCompanyLoginMode || isLoading) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('mode');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    if (currentUser || currentCompany || currentCustomer) return;
    setLoginContext('company');
    setShowLoginModal(true);
    // Only after the initial load settles — session state is final by then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Observability: record every questionnaire-link open, even when a
  // staff/company session silently swallows the URL params and the intended
  // questionnaire never renders. Fire once per page load, after the initial
  // load settles so session state (currentUser/currentCompany/currentCustomer)
  // is final.
  useEffect(() => {
    if (!isQuestionnaireMode || isLoading || linkEventSentRef.current) return;
    linkEventSentRef.current = true;
    const { sessionState, routeServed } = resolveLinkEventContext(
      currentUser,
      currentCompany,
      currentCustomer,
      Boolean(questionnaireAppointmentId)
    );
    api
      .reportLinkEvent({
        kind: 'questionnaire_link',
        appointmentRef: questionnaireAppointmentId || '',
        sessionState,
        routeServed
      })
      .catch(() => {
        /* observability only — never disturb the user */
      });
    // Fire once, after the initial load settles — session state is final by then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    setErrorReporterUserType(
      currentUser
        ? currentUser.role === 'technician'
          ? 'technician'
          : 'staff'
        : currentCompany
          ? 'company'
          : currentCustomer
            ? 'customer'
            : 'anonymous'
    );
  }, [currentUser, currentCompany, currentCustomer]);

  const openRecoveryCodeEntry = (email: string) => {
    setRecoveryEmail(email);
    setRecoveryCode('');
    setRecoveryStep('code');
    setRecoveryError('');
    setShowLoginModal(false);
    setShowRecoveryModal(true);
  };

  const openSignInCodeEntry = (email: string) => {
    setSignInEmail(email);
    setSignInCode('');
    setSignInError('');
    setShowLoginModal(false);
    setShowSignInCodeModal(true);
  };

  const handleSignInCancel = () => {
    setShowSignInCodeModal(false);
    setSignInCode('');
    setSignInError('');
  };

  const handleVerifySignInCode = async () => {
    if (!signInCode.trim()) {
      setSignInError('Please enter the code from your email.');
      return;
    }
    setIsVerifyingSignIn(true);
    setSignInError('');
    try {
      await api.verifyCustomerSignInCode(signInEmail, signInCode);
      // Session established → onAuthStateChange 'SIGNED_IN' logs them in.
      setShowSignInCodeModal(false);
      setSignInCode('');
    } catch (e: any) {
      setSignInError(e?.message || 'Invalid or expired code.');
    } finally {
      setIsVerifyingSignIn(false);
    }
  };

  const handleVerifyRecoveryCode = async () => {
    if (!recoveryCode.trim()) {
      setRecoveryError('Please enter the code from your email.');
      return;
    }
    setIsUpdatingRecovery(true);
    setRecoveryError('');
    try {
      await api.verifyRecoveryCode(recoveryEmail, recoveryCode);
      setRecoveryStep('password');
    } catch (e: any) {
      setRecoveryError(e?.message || 'Invalid or expired code.');
    } finally {
      setIsUpdatingRecovery(false);
    }
  };

  const handleRecoveryPasswordUpdate = async () => {
    if (recoveryPassword !== recoveryConfirmPassword) {
      setRecoveryError('Passwords do not match.');
      return;
    }
    const recoveryPolicyError = validatePasswordPolicy(recoveryPassword);
    if (recoveryPolicyError) {
      setRecoveryError(recoveryPolicyError);
      return;
    }

    setIsUpdatingRecovery(true);
    setRecoveryError('');

    try {
      const {
        data: { session: supabaseSession }
      } = await supabase.auth.getSession();
      if (!supabaseSession) {
        setRecoveryError(
          'Your password reset link has expired or was opened in a different browser tab. Please request a new reset link.'
        );
        return;
      }

      const sessionProfile = await api.getCurrentSession();
      if (sessionProfile?.type === 'company') {
        const memberId = sessionProfile.profile.loggedInMember?.id || 'primary';
        await api.updateCompanyPassword(sessionProfile.profile.id, memberId, recoveryPassword);
      } else if (sessionProfile?.type === 'customer') {
        await api.updateCustomerPassword(sessionProfile.profile.id, recoveryPassword);
      } else {
        const { error } = await supabase.auth.updateUser({ password: recoveryPassword });
        if (error) throw error;
      }

      await api.logout();
      clearRecoveryUrl();
      setShowRecoveryModal(false);
      setRecoveryPassword('');
      setRecoveryConfirmPassword('');
      showAlertModal('Password updated. Please sign in.');
      if (sessionProfile?.type) {
        setLoginContext(sessionProfile.type);
      }
      setShowLoginModal(true);
    } catch (e: any) {
      const msg: string = e?.message || '';
      if (msg.toLowerCase().includes('signal') || msg.toLowerCase().includes('abort')) {
        setRecoveryError(
          'Your password reset session has expired. Please close this and request a new reset link.'
        );
      } else {
        setRecoveryError(friendlyPasswordError(e));
      }
    } finally {
      setIsUpdatingRecovery(false);
    }
  };

  const handleCustomerPasswordUpdate = async () => {
    if (customerPassword !== customerConfirmPassword) {
      setCustomerPasswordError('Passwords do not match.');
      return;
    }
    const customerPolicyError = validatePasswordPolicy(customerPassword);
    if (customerPolicyError) {
      setCustomerPasswordError(customerPolicyError);
      return;
    }

    setIsUpdatingCustomerPassword(true);
    setCustomerPasswordError('');

    try {
      if (!currentCustomer) {
        throw new Error('Customer session not found.');
      }
      await api.updateCustomerPassword(currentCustomer.id, customerPassword);
      setShowCustomerPasswordGate(false);
      setCustomerPassword('');
      setCustomerConfirmPassword('');
      await hydrateSession();
      showAlertModal('Password updated. You can now use your portal.');
    } catch (e: any) {
      setCustomerPasswordError(friendlyPasswordError(e));
    } finally {
      setIsUpdatingCustomerPassword(false);
    }
  };

  const handleRecoveryCancel = async () => {
    await api.logout();
    clearRecoveryUrl();
    setShowRecoveryModal(false);
    setRecoveryPassword('');
    setRecoveryConfirmPassword('');
    setRecoveryError('');
    setRecoveryStep('code');
    setRecoveryCode('');
  };

  const handleLogout = async () => {
    await api.logout();
    setCurrentSessionId(null);
    setCurrentUser(null);
    setCurrentCompany(null);
    setCurrentCustomer(null);
    setShowCustomerPasswordGate(false);
    setStaffNameGateDismissed(false);
    setCustomerPassword('');
    setCustomerConfirmPassword('');
    setCustomerPasswordError('');
    setView('home');
  };

  const handleCurrentSessionRevoked = async () => {
    setCurrentSessionId(null);
    setCurrentUser(null);
    setCurrentCompany(null);
    setCurrentCustomer(null);
    setShowCustomerPasswordGate(false);
    setStaffNameGateDismissed(false);
    setShowLoginModal(false);
    setView('home');
    await supabase.auth.signOut();
  };

  const handleHeaderLogoClick = () => {
    setShowLoginModal(false);
    setShowRecoveryModal(false);
    setView('home');
  };

  const handleLoginSuccess = (u: User | null, c: Company | null, cust: Customer | null) => {
    if (u) {
      setCurrentUser(u);
      setCurrentCompany(null);
      setCurrentCustomer(null);
    }
    if (c) {
      setCurrentCompany(c);
      setCurrentUser(null);
      setCurrentCustomer(null);
    }
    if (cust) {
      setCurrentCustomer(cust);
      setCurrentUser(null);
      setCurrentCompany(null);
    }
    setShowLoginModal(false);
    loadData(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <div className="bg-white p-8 rounded-3xl shadow-xl flex flex-col items-center border border-slate-100">
          <Loader2 className="w-12 h-12 text-teal-600 animate-spin mb-4" />
          <p className="font-black text-slate-800 uppercase tracking-widest text-xs">
            Connecting to Database...
          </p>
        </div>
      </div>
    );
  }

  if (isVerifyMode) return <VerificationPage systemConfig={systemConfig} />;

  return (
    <Layout
      currentUser={currentUser}
      currentCompany={currentCompany}
      onLogout={handleLogout}
      onLoginClick={() => {
        setLoginContext('staff');
        setShowLoginModal(true);
      }}
      onLogoClick={handleHeaderLogoClick}
      appName={systemConfig?.general?.appName}
      logoUrl={systemConfig?.general?.logoUrl}
    >
      {startupError && anyDataLoaded && (
        <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-center gap-4 text-xs font-black uppercase tracking-widest sticky top-16 z-[46]">
          <AlertTriangle className="w-4 h-4" />
          {startupError.message}
          {formatErrorRef(startupError.requestId) && (
            <span className="font-mono normal-case opacity-80">
              Ref: {formatErrorRef(startupError.requestId)}
            </span>
          )}
          <button
            onClick={() => loadData(true)}
            className="ml-2 underline opacity-90 hover:opacity-100"
          >
            Retry
          </button>
          <button
            onClick={() => setStartupError(null)}
            className="ml-2 opacity-50 hover:opacity-100"
          >
            Dismiss
          </button>
        </div>
      )}

      {startupError && !anyDataLoaded && (
        <div className="min-h-[60vh] flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center border border-slate-100">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Can't reach the server</h2>
            <p className="text-slate-500 mb-2">{startupError.message}</p>
            {formatErrorRef(startupError.requestId) && (
              <p className="text-xs font-mono text-slate-400 mb-6">
                Ref: {formatErrorRef(startupError.requestId)}
              </p>
            )}
            {!formatErrorRef(startupError.requestId) && <div className="mb-6" />}
            <button
              onClick={() => loadData(true)}
              className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {Object.keys(apiErrors).length > 0 && currentUser && (
        <div className="bg-red-600 text-white px-4 py-2 flex items-center justify-center gap-4 text-xs font-black uppercase tracking-widest sticky top-16 z-[45]">
          <ShieldAlert className="w-4 h-4" />
          Database Sync Errors Detected. Check Admin {'>'} Settings for Diagnostics.
          <button onClick={() => setApiErrors({})} className="ml-4 opacity-50 hover:opacity-100">
            Dismiss
          </button>
        </div>
      )}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        context={loginContext}
        users={users}
        companies={companies}
        customers={customers}
        onLoginSuccess={handleLoginSuccess}
        onForgotPassword={openRecoveryCodeEntry}
        onSignInLinkSent={openSignInCodeEntry}
        onSwitchContext={setLoginContext}
      />
      {showCustomerPasswordGate && currentCustomer && (
        <div className="fixed inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-50 p-4 animate-fade-in text-slate-900">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full relative border border-slate-200">
            <h3 className="font-bold text-lg text-slate-800 mb-1">Set Your Password</h3>
            <p className="text-xs text-slate-500 mb-4">
              Please set a password to finish activating your patient portal access.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  New Password
                </label>
                <PasswordInput
                  className="rounded-lg p-2 text-sm focus:ring-teal-500"
                  value={customerPassword}
                  onChange={(e) => {
                    setCustomerPassword(e.target.value);
                    setCustomerPasswordError('');
                  }}
                />
                <p className="text-[10px] text-slate-400 mt-1">{PASSWORD_POLICY_HINT}</p>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  Confirm Password
                </label>
                <PasswordInput
                  className="rounded-lg p-2 text-sm focus:ring-teal-500"
                  value={customerConfirmPassword}
                  onChange={(e) => {
                    setCustomerConfirmPassword(e.target.value);
                    setCustomerPasswordError('');
                  }}
                />
              </div>
              {customerPasswordError && (
                <p className="text-red-600 text-[10px] font-bold bg-red-50 p-2 rounded border border-red-100">
                  {customerPasswordError}
                </p>
              )}
              <button
                onClick={handleCustomerPasswordUpdate}
                disabled={isUpdatingCustomerPassword}
                className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isUpdatingCustomerPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                  </>
                ) : (
                  'Save Password'
                )}
              </button>
              <button
                onClick={handleLogout}
                className="w-full text-slate-500 text-xs font-bold hover:text-slate-700"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
      {currentUser && staffNameNeedsSetup(currentUser) && !staffNameGateDismissed && (
        <StaffNameGate
          user={currentUser}
          onSaved={(name) => {
            setCurrentUser({ ...currentUser, name });
            setUsers((prev) => prev.map((u) => (u.id === currentUser.id ? { ...u, name } : u)));
          }}
          onSkip={() => setStaffNameGateDismissed(true)}
          onLogout={handleLogout}
        />
      )}
      {showSignInCodeModal && (
        <div className="fixed inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-50 p-4 animate-fade-in text-slate-900">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full relative border border-slate-200">
            <h3 className="font-bold text-lg text-slate-800 mb-1">Sign In</h3>
            <p className="text-xs text-slate-500 mb-4">
              Enter the code we emailed you to sign in to your portal.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  Sign-in Code
                </label>
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="w-full rounded-lg p-2 text-sm border border-slate-300 focus:ring-teal-500 focus:border-teal-500 tracking-widest"
                  value={signInCode}
                  onChange={(e) => {
                    setSignInCode(e.target.value);
                    setSignInError('');
                  }}
                  placeholder="Enter code"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  Type the code from your email exactly as it appears.
                </p>
              </div>
              {signInError && (
                <p className="text-red-600 text-[10px] font-bold bg-red-50 p-2 rounded border border-red-100">
                  {signInError}
                </p>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={handleSignInCancel}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded"
                  disabled={isVerifyingSignIn}
                >
                  Cancel
                </button>
                <button
                  onClick={handleVerifySignInCode}
                  disabled={isVerifyingSignIn}
                  className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isVerifyingSignIn ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showRecoveryModal && (
        <div className="fixed inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-50 p-4 animate-fade-in text-slate-900">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full relative border border-slate-200">
            <h3 className="font-bold text-lg text-slate-800 mb-1">Reset Password</h3>
            <p className="text-xs text-slate-500 mb-4">
              {recoveryStep === 'code'
                ? 'Enter the code we emailed you to continue.'
                : 'Set a new password to continue to your portal.'}
            </p>
            {recoveryStep === 'code' ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                    Reset Code
                  </label>
                  <input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className="w-full rounded-lg p-2 text-sm border border-slate-300 focus:ring-teal-500 focus:border-teal-500 tracking-widest"
                    value={recoveryCode}
                    onChange={(e) => {
                      setRecoveryCode(e.target.value);
                      setRecoveryError('');
                    }}
                    placeholder="Enter code"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    Type the code from your email exactly as it appears.
                  </p>
                </div>
                {recoveryError && (
                  <p className="text-red-600 text-[10px] font-bold bg-red-50 p-2 rounded border border-red-100">
                    {recoveryError}
                  </p>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={handleRecoveryCancel}
                    className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded"
                    disabled={isUpdatingRecovery}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleVerifyRecoveryCode}
                    disabled={isUpdatingRecovery}
                    className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isUpdatingRecovery ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
                      </>
                    ) : (
                      'Verify code'
                    )}
                  </button>
                </div>
              </div>
            ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  New Password
                </label>
                <PasswordInput
                  className="rounded-lg p-2 text-sm focus:ring-teal-500"
                  value={recoveryPassword}
                  onChange={(e) => {
                    setRecoveryPassword(e.target.value);
                    setRecoveryError('');
                  }}
                />
                <p className="text-[10px] text-slate-400 mt-1">{PASSWORD_POLICY_HINT}</p>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  Confirm Password
                </label>
                <PasswordInput
                  className="rounded-lg p-2 text-sm focus:ring-teal-500"
                  value={recoveryConfirmPassword}
                  onChange={(e) => {
                    setRecoveryConfirmPassword(e.target.value);
                    setRecoveryError('');
                  }}
                />
              </div>
              {recoveryError && (
                <p className="text-red-600 text-[10px] font-bold bg-red-50 p-2 rounded border border-red-100">
                  {recoveryError}
                </p>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={handleRecoveryCancel}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded"
                  disabled={isUpdatingRecovery}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecoveryPasswordUpdate}
                  disabled={isUpdatingRecovery}
                  className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isUpdatingRecovery ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </div>
            </div>
            )}
          </div>
        </div>
      )}

      {currentUser && currentUser.role !== 'technician' && (
        <AdminDashboard
          isRefreshing={isRefreshing}
          currentUser={currentUser}
          currentSessionId={currentSessionId}
          services={services}
          venues={venues}
          addOns={addOns}
          userGroups={userGroups}
          companies={companies}
          customers={customers}
          questionnaires={questionnaires}
          submissions={submissions}
          systemConfig={systemConfig}
          onUpdateSystemConfig={(c) => api.updateSystemConfig(c).then(() => loadData(true))}
          onCurrentSessionRevoked={handleCurrentSessionRevoked}
          onForceRefresh={() => loadData(true)}
        />
      )}

      {currentUser && currentUser.role === 'technician' && (
        <TechnicianView
          currentUser={currentUser}
          services={services}
          addOns={addOns}
          venues={venues}
          questionnaires={questionnaires}
          submissions={submissions}
          certificates={certificates}
          systemConfig={systemConfig ?? undefined}
          onUpdateUser={(u) => setCurrentUser(u)}
          onIssueCertificate={(c) =>
            api.issueCertificate(c).then(() => {
              loadData(true);
              return { success: true };
            })
          }
          onAmendCertificate={async (id, fields) => {
            const result = await api.amendCertificate(id, fields);
            await loadData(true);
            return { success: true, certificate: result.certificate };
          }}
        />
      )}

      {currentCompany && (
        <CompanyDashboard
          isRefreshing={isRefreshing}
          company={currentCompany}
          appointments={appointments}
          services={services}
          venues={venues}
          addOns={addOns}
          certificates={certificates}
          systemConfig={systemConfig}
          termsAndConditions={systemConfig?.termsAndConditions || ''}
          onBookNew={async (app) => {
            await api.createBooking(app);
            await loadData(true);
          }}
          onUpdateCompany={(c) => api.updateCompany(c).then(() => loadData(true))}
          onForceRefresh={() => loadData(true)}
        />
      )}

      {currentCustomer && (
        <CustomerPortal
          customer={currentCustomer}
          appointments={appointments}
          services={services}
          venues={venues}
          addOns={addOns}
          questionnaires={questionnaires}
          submissions={submissions}
          certificates={certificates}
          systemConfig={systemConfig}
          onLogout={handleLogout}
          onSubmitQuestionnaire={(s) => api.submitQuestionnaire(s).then(() => loadData(true))}
          onEditSubmission={(id, answers) =>
            api.editSubmissionAnswers(id, answers).then(() => loadData(true))
          }
          onUpdateBooking={(b) => api.updateBooking(b).then(() => loadData(true))}
          onForceRefresh={() => loadData(true)}
          initialQuestionnaireAppId={questionnaireAppointmentId}
          isDataSettled={anyDataLoaded && !isLoading && !isRefreshing}
        />
      )}

      {!currentUser &&
        !currentCompany &&
        !currentCustomer &&
        !(startupError && !anyDataLoaded) &&
        (view === 'booking' ? (
          <BookingFlow
            venues={venues}
            services={services}
            addOns={addOns}
            appointments={appointments}
            termsAndConditions={systemConfig?.termsAndConditions || ''}
            onComplete={async (app) => {
              await api.createBooking(app);
              setView('home');
              await loadData(true);
            }}
            onBack={() => setView('home')}
          />
        ) : (
          <LandingPage
            systemConfig={systemConfig}
            onNavigateBooking={() => setView('booking')}
            onLoginClick={(ctx) => {
              setLoginContext(ctx);
              setShowLoginModal(true);
            }}
          />
        ))}
      <AlertModalHost />
      <ToastHost />
    </Layout>
  );
}
